import React, { useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import { Box, Icon, Stack, Text, Flex } from '@tonic-ui/react';
import { countries } from '../../utils/countries';
import ExternalLink from '../../components/external-link/external-link';

import {
  DropdownMenu,
  FormContainer,
  InputField,
  bspSupportFormValidation,
  SubmitButton,
} from '../../components/forms';
import { getC1UserDetails } from '../../store';

type BSPSupportAuthenticatedFormProps = {
  onSubmit: (supportData: BSPSupportRequestData) => Promise<void>;
};

const BSPSupportFormFields = (): JSX.Element => {
  const { t } = useTranslation('', { keyPrefix: 'bspSupport.supportPage' });
  const { isValid, dirty, errors } = useFormikContext<BSPSupportFormValues>();

  return (
    <>
      <Flex
        direction="column"
        flex="3"
        paddingTop="5x"
        paddingLeft="5x"
        height="100%"
        maxWidth="450px"
      >
        <Stack spacing="4x" marginBottom="5x">
          <Text size="2xl">{t('pageTitle')}</Text>
          <Text size="md">{t('pageDescription')}</Text>
        </Stack>
        <Stack spacing="5x">
          <InputField
            id="firstName"
            type="text"
            name="firstName"
            label={t('firstNameTextLabel') as string}
            isRequired={true}
          />
          <InputField
            id="lastName"
            type="text"
            name="lastName"
            label={t('lastNameTextLabel') as string}
            isRequired={true}
          />
          <Box>
            <InputField id="email" type="email" label={t('email')} name="email" isRequired={true} />
          </Box>
          <DropdownMenu
            options={countries}
            label={t('country')}
            placeholder={t('selectCountry')}
            name="country"
            maxHeight={200}
            isRequired={true}
            isSearchable={true}
          />
        </Stack>
        <Flex py="4x" justifyContent="flex-end">
          <SubmitButton
            variant="primary"
            id="bsp-support-form-submit"
            label={t('submitButtonText')}
            disabled={!(isValid && dirty)}
            width="100%"
          />
        </Flex>
        <Stack spacing="5x" direction="row" columnGap="0.3em">
          <Text>{t('alreadySignedUp')} </Text>
          <ExternalLink
            href={t('alreadySignedUpLink')}
            showIcon={false}
            key={`bsp-link`}
            textDecoration="none"
          >
            {t('alreadySignedUpLinkText')}
          </ExternalLink>
        </Stack>
      </Flex>
    </>
  );
};

const BSPSupportAuthenticatedForm = ({
  onSubmit,
}: BSPSupportAuthenticatedFormProps): JSX.Element => {
  const { userEmail } = useSelector(
    (state) => ({
      userEmail: (getC1UserDetails(state, 'email') ??
        getC1UserDetails(state, 'wsEmailAddress')) as string,
    }),
    shallowEqual
  );

  const initialValues: BSPSupportFormValues = {
    email: userEmail,
    firstName: '',
    lastName: '',
    country: '',
  };

  return (
    <FormContainer
      name="bspSupportFormAuth"
      id="bsp-support-form-authenticated"
      data-id="bsp-support-form-authenticated"
      validateOnBlur={true}
      validateOnChange={true}
      validateOnMount={true}
      enableReinitialize={true}
      initialValues={initialValues}
      customValidation={bspSupportFormValidation}
      submitAction={(formValues) =>
        onSubmit({
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          email: formValues.email,
          country: formValues.country,
        } as BSPSupportRequestData)
      }
    >
      <BSPSupportFormFields />
    </FormContainer>
  );
};

BSPSupportAuthenticatedForm.displayName = 'BSPSupportAuthenticatedForm';

export default BSPSupportAuthenticatedForm;
